import { DocumentTileData, Store } from '@evertel/types'
import { decorate, injectable } from '@evertel/di'
import moment from 'moment'

// what a tile shows in place of a document this user can no longer see. Matches the text the
// API writes into a share message when the share is removed from the room
const DOCUMENT_NO_ACCESS_TEXT = 'EverDoc access has been removed.'

interface DocumentTileWithFetchDate extends Partial<DocumentTileData> {
    /** the tile is identified by the document it was compiled from */
    id?: number
    fetchedDate?: moment.Moment
    /** the tile could not be fetched because this user no longer has access to the document */
    noAccess?: boolean
}

class DocumentTileStore extends Store<DocumentTileWithFetchDate> {
    // one request per document at a time: tiles for the same document (a share and its
    // update messages) that mount together wait on the first one instead of each fetching
    private _inflightFetches = new Map<number, Promise<unknown>>()

    constructor() {
        super(['id', 'documentId', 'name', 'isArchived', 'createdDate', 'updatedDate', 'owner', 'departmentShortName', 'schema', 'fields', 'fetchedDate', 'noAccess'])
    }

    update(data: DocumentTileData | DocumentTileData[] | undefined) {
        if (data === undefined) return

        const tiles = (Array.isArray(data) ? data : [data])
            .filter(tile => tile?.documentId)
            // a tile that was fetched is accessible, so any earlier no-access marker is cleared
            .map(tile => ({ ...tile, id: tile.documentId, fetchedDate: moment(), noAccess: false }))

        return super.update(tiles)
    }

    /**
     * Records that this user can no longer see the document, so its tiles stop fetching.
     * Merged rather than replaced: whatever was cached stays behind the marker, which is what
     * tiles check first, and there is never a render with no entry at all in between
     */
    setNoAccess(documentId: number) {
        if (!documentId) return

        super.update({ id: documentId, documentId, noAccess: true, fetchedDate: moment() })
    }

    getInflightFetch(documentId: number) {
        return this._inflightFetches.get(documentId)
    }

    setInflightFetch(documentId: number, promise: Promise<unknown>) {
        this._inflightFetches.set(documentId, promise)
    }

    clearInflightFetch(documentId: number) {
        this._inflightFetches.delete(documentId)
    }
}

decorate(injectable(), DocumentTileStore)

export { DocumentTileStore, DocumentTileWithFetchDate, DOCUMENT_NO_ACCESS_TEXT }
